import {
  ACHResponse,
  CreditCardResponse,
  PaymentPlanFrequency,
  PaymentPlanResponse,
  PaymentsService,
  StudentLimitedResponse,
  StudentsService,
} from "client/openapi";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import { Dialog } from "components/Dialog";
import AutomaticPaymentsModal from "pages/manage/students/[id]/PaymentsTab/Modals/Charges/PayPerSession";
import { useEffect, useState } from "react";
import { StepDialogProps, StepFrame } from "./shared";

function plural(n: number, word: string) {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/** How often the plan bills, as the billing page sets it. */
export function cadence(plan: PaymentPlanResponse): string {
  if (plan.frequency === PaymentPlanFrequency.NONE) return "Off";
  const unit = plan.frequency === PaymentPlanFrequency.WEEKLY ? "week" : "day";
  return `Every ${plural(plan.interval, unit)}`;
}

/** The plan's payment method, in the billing page's words. */
function methodLabel(
  plan: PaymentPlanResponse,
  cards: CreditCardResponse[],
  banks: ACHResponse[]
): string {
  if (plan.invoice_email) return `Invoice to ${plan.invoice_email}`;
  const card = cards.find(
    (c) => c.payment_method_id === plan.payment_method_id
  );
  if (card) return `Card: ${card.brand} ending ${card.last_four}`;
  const bank = banks.find(
    (b) => b.payment_method_id === plan.payment_method_id
  );
  if (bank) return `Bank account: ${bank.bank_name} ending ${bank.last_four}`;
  return plan.payment_method_id ? "A saved payment method" : "None";
}

/**
 * The Payment plan step (students). Not yet: the billing page's Automatic
 * Billing settings for the student, the same window. Done: the plan (how
 * often, pre-billing, the method), with Change to open those settings.
 */
export default function PaymentPlanStepDialog({
  orgId,
  person,
  done,
  onDone,
  onClose,
}: StepDialogProps) {
  const [student, setStudent] = useState<StudentLimitedResponse>();
  const [plan, setPlan] = useState<PaymentPlanResponse>();
  const [method, setMethod] = useState<string>();
  const [editing, setEditing] = useState(!done);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const studentId = person.id;
    StudentsService.getStudent({ studentId })
      .then((found) => setStudent(found as StudentLimitedResponse))
      .catch(() => setFailed(true));
    StudentsService.getPaymentPlan({ studentId })
      .then(setPlan)
      .catch(() => undefined); // no plan yet
  }, [person.id]);

  useEffect(() => {
    if (!plan || !done || !plan.payment_method_id) return;
    const studentId = person.id;
    Promise.all([
      PaymentsService.getStudentCards({ studentId }),
      PaymentsService.getStudentAchAccounts({ studentId }),
    ])
      .then(([cards, banks]) => setMethod(methodLabel(plan, cards, banks)))
      .catch(() => setMethod("A saved payment method"));
  }, [plan, done, person.id]);

  const saved = (next: PaymentPlanResponse) => {
    setPlan(next);
    onDone();
  };

  if (editing) {
    if (!student) {
      return (
        <StepFrame title="Payment plan" person={person} onClose={onClose}>
          <p className={`text-sm ${failed ? "text-red-600" : "text-gray-500"}`}>
            {failed
              ? "The student could not be loaded. Try again."
              : "Loading..."}
          </p>
        </StepFrame>
      );
    }
    return (
      <Dialog
        open
        onOpenChange={(open) => {
          if (open) return;
          if (done) setEditing(false);
          else onClose();
        }}
      >
        <AutomaticPaymentsModal
          student={student}
          orgId={orgId}
          paymentPlan={plan}
          setPaymentPlan={saved}
        />
      </Dialog>
    );
  }

  return (
    <StepFrame title="Payment plan" person={person} onClose={onClose}>
      {!plan ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : (
        <dl className="divide-y divide-gray-100 rounded-lg border border-gray-200">
          {[
            ["Bills automatically", cadence(plan)],
            [
              "Pre-billing",
              plan.pre_billing_enabled
                ? `On, ${plural(
                    plan.pre_billing_lookahead_weeks,
                    "week"
                  )} ahead`
                : "Off",
            ],
            [
              "Payment method",
              plan.payment_method_id
                ? method ?? "Loading..."
                : methodLabel(plan, [], []),
            ],
          ].map(([label, value]) => (
            <div
              key={label}
              className="grid grid-cols-[160px_1fr] gap-3 px-4 py-2.5"
            >
              <dt className="text-sm text-gray-500">{label}</dt>
              <dd className="text-sm text-gray-900">{value}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className="mt-4 flex justify-end">
        <Button
          color={ButtonColor.GREEN}
          size={ButtonSize.SMALL}
          disabled={!student}
          onClick={() => setEditing(true)}
        >
          Change
        </Button>
      </div>
    </StepFrame>
  );
}
